import { useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/base/EmptyState";
import ActionNotice from "@/components/base/ActionNotice";
import { usePageMeta } from "@/hooks/usePageMeta";
import { communityTopics, topicCategories } from "@/mocks/topics";
import { formatDate } from "@/utils/format";

const sortOptions = ["最新发布", "最多回复"];

export default function CommunityPage() {
  usePageMeta({
    title: "社区话题 - 成人网",
    description:
      "成人网社区话题列表，讨论作品偏好、观影习惯、行业话题与创作交流。支持按最新发布或最多回复排序，参与人数实时展示。",
    keywords: "成人社区,话题讨论,作品讨论,观影习惯,成人网",
  });

  const [category, setCategory] = useState("全部");
  const [sort, setSort] = useState(sortOptions[0]);
  const [page, setPage] = useState(1);
  const [composerOpen, setComposerOpen] = useState(false);
  const [draftTitle, setDraftTitle] = useState("");
  const [draftBody, setDraftBody] = useState("");
  const [notice, setNotice] = useState("");

  const filtered = useMemo(() => {
    const list = communityTopics.filter(
      (topic) => category === "全部" || topic.category === category,
    );
    if (sort === "最多回复") {
      return [...list].sort((a, b) => b.replyCount - a.replyCount);
    }
    return [...list].sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
  }, [category, sort]);

  const perPage = 4;
  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const currentPage = Math.min(page, totalPages);
  const items = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);

  const submitTopic = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!draftTitle.trim()) {
      return;
    }
    setComposerOpen(false);
    setDraftTitle("");
    setDraftBody("");
    setNotice("演示模式：话题未真正发布，站内不会保存任何数据");
  };

  return (
    <div className="container-page py-7">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "社区话题" }]} />

      <div className="mb-7 flex flex-col gap-4 border-b border-background-200 pb-6 md:flex-row md:items-end md:justify-between">
        <div>
          <span className="text-[11px] uppercase tracking-[0.28em] text-accent-500">Community</span>
          <h1 className="mt-2 font-heading text-2xl text-foreground-950 md:text-[32px]">
            社区话题
          </h1>
          <p className="mt-2 max-w-2xl text-[13px] leading-relaxed text-foreground-500">
            聊作品，也聊观看这件事本身。请保持礼貌与克制，不要发布联系方式、真实个人信息或露骨描述。
          </p>
        </div>
        <button
          type="button"
          onClick={() => setComposerOpen(true)}
          className="flex items-center justify-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap cursor-pointer"
        >
          <i className="ri-add-line"></i>
          发起话题
        </button>
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="min-w-0">
          <div className="mb-4 flex flex-wrap items-center gap-2">
            {topicCategories.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => {
                  setCategory(item);
                  setPage(1);
                }}
                className={`rounded-full px-3.5 py-1.5 text-[11.5px] transition-colors cursor-pointer ${
                  category === item
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-500 hover:text-foreground-900"
                }`}
              >
                {item}
              </button>
            ))}
            <div className="ml-auto flex items-center gap-1 rounded-full bg-background-100 p-1">
              {sortOptions.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => {
                    setSort(item);
                    setPage(1);
                  }}
                  className={`rounded-full px-2.5 py-1 text-[11px] transition-colors cursor-pointer whitespace-nowrap ${
                    sort === item
                      ? "bg-accent-500 text-background-50"
                      : "text-foreground-500 hover:text-foreground-900"
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>

          {items.length > 0 ? (
            <>
              <div className="panel divide-y divide-background-200">
                {items.map((topic, index) => (
                  <Link
                    key={topic.id}
                    to={`/community-detail/${topic.id}`}
                    className="group block px-4 py-4 transition-colors hover:bg-background-200/30"
                  >
                    <div className="flex items-start gap-3">
                      <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-background-200 text-[11px] text-accent-500">
                        {topic.author.slice(0, 1)}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded-sm bg-background-200 px-1.5 py-0.5 text-[10px] text-foreground-500">
                            {topic.category}
                          </span>
                          <h2 className="text-[14px] font-medium text-foreground-950 transition-colors group-hover:text-accent-500">
                            {topic.title}
                          </h2>
                        </div>
                        <p className="mt-1.5 line-clamp-2-safe text-xs leading-relaxed text-foreground-500">
                          {topic.excerpt}
                        </p>
                        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-foreground-500">
                          <span>{topic.author}</span>
                          <span>{formatDate(topic.createdAt.slice(0, 10))}</span>
                          <span className="flex items-center gap-1">
                            <i className="ri-chat-1-line"></i>
                            {topic.replyCount} 回复
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-team-line"></i>
                            {topic.participantCount} 参与
                          </span>
                          <span className="hidden text-foreground-600 sm:inline">
                            最后回复 {formatDate(topic.lastReplyAt.slice(0, 10))}
                          </span>
                        </div>
                      </div>
                      <span className="mt-0.5 hidden shrink-0 items-center gap-1 text-[11px] text-foreground-600 sm:flex">
                        {index + 1 === 1 && sort === "最多回复" ? (
                          <i className="ri-fire-line text-accent-500"></i>
                        ) : null}
                        <i className="ri-arrow-right-s-line transition-transform group-hover:translate-x-0.5 group-hover:text-accent-500"></i>
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
              <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
            </>
          ) : (
            <EmptyState
              icon="ri-chat-3-line"
              title="这个分类下还没有话题"
              description="换个分类看看，或者成为第一个发起讨论的人。"
              actionLabel="查看全部话题"
              onAction={() => {
                setCategory("全部");
                setPage(1);
              }}
            />
          )}
        </div>

        <aside className="min-w-0 space-y-5">
          <section className="panel p-4">
            <h2 className="flex items-center gap-2 text-sm font-medium text-foreground-950">
              <i className="ri-shield-check-line text-accent-500"></i>
              社区准则
            </h2>
            <ul className="mt-3 space-y-2.5 text-[11.5px] leading-relaxed text-foreground-500">
              <li className="flex gap-2">
                <i className="ri-check-line mt-0.5 text-accent-600"></i>
                尊重不同偏好，不攻击、不评判他人选择。
              </li>
              <li className="flex gap-2">
                <i className="ri-check-line mt-0.5 text-accent-600"></i>
                不发布真实姓名、住址、联系方式等个人信息。
              </li>
              <li className="flex gap-2">
                <i className="ri-check-line mt-0.5 text-accent-600"></i>
                不传播未授权的影像内容，不发布露骨描述。
              </li>
              <li className="flex gap-2">
                <i className="ri-check-line mt-0.5 text-accent-600"></i>
                发现违规内容可通过“关于我们”页面的表单反馈。
              </li>
            </ul>
          </section>

          <section className="panel p-4">
            <h2 className="text-sm font-medium text-foreground-950">热门标签</h2>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {["作品讨论", "氛围感", "黑白摄影", "独立制片", "分级制度", "夜间用眼"].map(
                (tag) => (
                  <Link
                    key={tag}
                    to={`/search?q=${encodeURIComponent(tag)}`}
                    className="rounded-full bg-background-200 px-2.5 py-1 text-[11px] text-foreground-500 transition-colors hover:text-accent-500"
                  >
                    #{tag}
                  </Link>
                ),
              )}
            </div>
          </section>

          <section className="rounded-lg border border-primary-800/60 bg-primary-950/40 p-4">
            <h2 className="text-sm font-medium text-foreground-950">温馨提示</h2>
            <p className="mt-2 text-[11px] leading-relaxed text-foreground-500">
              社区的发帖与回复均为前端演示，内容不会上传服务器，刷新页面后即会消失。
            </p>
          </section>
        </aside>
      </div>

      {composerOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-background-950/75 animate-fade-in"
            onClick={() => setComposerOpen(false)}
          ></div>
          <form
            onSubmit={submitTopic}
            className="relative w-full max-w-lg animate-fade-up rounded-xl border border-background-200 bg-background-50 p-5"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-medium text-foreground-950">发起话题（演示）</h2>
              <button
                type="button"
                onClick={() => setComposerOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-md border border-background-300 text-foreground-500 cursor-pointer"
                aria-label="关闭"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
            <p className="mt-2 text-[11px] leading-relaxed text-foreground-500">
              仅作界面演示，提交后不会真正发布，也不会保存任何内容。
            </p>

            <label className="mt-4 block text-[11px] text-foreground-500" htmlFor="topic-title">
              话题标题
            </label>
            <input
              id="topic-title"
              value={draftTitle}
              onChange={(event) => setDraftTitle(event.target.value)}
              maxLength={60}
              placeholder="用一句话说清你想聊什么"
              className="mt-1.5 w-full rounded-md border border-background-300 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-600 focus:border-accent-500 focus:outline-none"
            />

            <label className="mt-4 block text-[11px] text-foreground-500" htmlFor="topic-body">
              正文
            </label>
            <textarea
              id="topic-body"
              value={draftBody}
              onChange={(event) => setDraftBody(event.target.value)}
              maxLength={500}
              rows={5}
              placeholder="补充一些背景，让大家更容易接话（最多 500 字）"
              className="mt-1.5 w-full resize-none rounded-md border border-background-300 bg-background-100 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-600 focus:border-accent-500 focus:outline-none"
            />

            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setComposerOpen(false)}
                className="rounded-md border border-background-300 px-4 py-2 text-xs text-foreground-600 transition-colors hover:border-background-400 whitespace-nowrap cursor-pointer"
              >
                取消
              </button>
              <button
                type="submit"
                className="rounded-md bg-primary-500 px-4 py-2 text-xs text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap cursor-pointer"
              >
                提交演示
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {notice ? <ActionNotice message={notice} onClose={() => setNotice("")} /> : null}
    </div>
  );
}